import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { STORY_MEDIA_MAX_BYTES, STORY_TITLE_MAX } from '@dorixona/api/stories'
import { nowMs, withCountRu } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { Modal } from '../../ui/Modal'
import { DetailHead } from '../../ui/DetailHead'
import { DatePicker } from '../../ui/DatePicker'
import { Failed, Loading } from '../../ui/Load'
import { useSession } from '../auth/session'
import {
  useDeleteStory,
  useSaveStory,
  useStories,
  useUploadStoryMedia,
  type Story,
  type StoryToSave,
} from './api'
import {
  STATUS_TEXT,
  STATUS_TONE,
  IMAGE_ACCEPT,
  linkToSave,
  newSlide,
  newStory,
  periodError,
  periodFromInput,
  periodText,
  periodToInput,
  slideError,
  storyFailureText,
  storyStatus,
  totalSeconds,
} from './model'
import { StoryCircle } from './StoryCircle'
import { StorySlides, type DraftSlide } from './StorySlides'

export interface StoryScreenProps {
  onToast: (message: string) => void
}

/** Всё, что в карточке сохраняется одной кнопкой. */
interface Draft {
  titleRu: string
  titleUz: string
  coverKey: string
  coverUrl: string
  slides: DraftSlide[]
  from: string
  to: string
  /** Число — строкой: пока человек печатает, поле бывает пустым. */
  sort: string
}

function toDraft(story: Story): Draft {
  return {
    titleRu: story.titleRu,
    titleUz: story.titleUz,
    coverKey: story.coverKey,
    coverUrl: story.coverUrl,
    // У сохранённого слайда id есть всегда.
    slides: story.slides.map((slide) => ({ ...slide, uid: `s${String(slide.id)}` })),
    ...periodToInput(story),
    sort: String(story.sort),
  }
}

function changed(draft: Draft, story: Story): boolean {
  return JSON.stringify(draft) !== JSON.stringify(toDraft(story))
}

/** Что мешает сохранить — первая причина; пусто, если сохранить можно. */
function draftError(draft: Draft): string {
  if (!draft.titleRu.trim()) return 'Укажите подпись на русском'
  const period = periodError(draft.from, draft.to)
  if (period) return period
  const bad = draft.slides.findIndex((slide) => slideError(slide) !== '')
  return bad >= 0 ? `Слайд ${bad + 1}: ${slideError(draft.slides[bad] as DraftSlide)}` : ''
}

interface CoverProps {
  draft: Draft
  canUpdate: boolean
  onUpload: (file: File) => void
  onRemove: () => void
  busy: boolean
}

/**
 * Обложка в кружке на главной. Без неё в кружке первый слайд — обложку
 * ставят, когда слайд не читается в маленьком круге.
 */
function StoryCover({ draft, canUpdate, onUpload, onRemove, busy }: CoverProps) {
  const [error, setError] = useState('')
  // Своя обложка — та, что загрузили: у неё есть ключ файла.
  const own = draft.coverKey !== ''
  const [depth, setDepth] = useState(0)

  function take(file: File | undefined) {
    if (!file) return
    if (!IMAGE_ACCEPT.split(',').includes(file.type)) {
      setError('Нужна картинка JPEG, PNG или WebP')
      return
    }
    if (file.size > STORY_MEDIA_MAX_BYTES) {
      setError('Файл больше 5 МБ')
      return
    }
    setError('')
    onUpload(file)
  }

  const dropZone = canUpdate
    ? {
        onDragEnter: (event: DragEvent) => {
          event.preventDefault()
          setDepth((d) => d + 1)
        },
        onDragOver: (event: DragEvent) => {
          event.preventDefault()
        },
        onDragLeave: () => {
          setDepth((d) => d - 1)
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault()
          setDepth(0)
          if (!busy) take(event.dataTransfer.files[0])
        },
      }
    : {}

  return (
    <section
      className="ui-card ui-drop-card"
      aria-labelledby="sto-cover"
      aria-busy={busy}
      data-drop={depth > 0 || undefined}
      {...dropZone}
    >
      <div className="ui-card-head">
        <h3 id="sto-cover">Обложка</h3>
        {busy ? <span className="aside">Загружаем…</span> : null}
      </div>
      <div className="ui-card-body sto-cover-body">
        <StoryCircle
          // Без своей обложки сервер присылает адресом первый слайд — а в
          // черновике первым может стоять уже другой.
          story={{
            titleRu: draft.titleRu,
            coverUrl: own ? draft.coverUrl : '',
            slides: draft.slides,
          }}
          size={88}
          live
        />
        <div className="sto-cover-side">
          <span className="sto-cover-cap">{draft.titleRu || 'Подпись'}</span>
          <span className="ui-hint">
            {own ? 'Своя обложка' : 'Без обложки в кружке первый слайд'}
          </span>
          {canUpdate ? (
            <div className="sto-cover-actions">
              <label className="ui-btn" data-variant="secondary" data-size="sm">
                <Icon name="i-img" />
                {own ? 'Заменить' : 'Загрузить'}
                <input
                  type="file"
                  className="visually-hidden"
                  accept={IMAGE_ACCEPT}
                  aria-label={own ? 'Заменить обложку' : 'Загрузить обложку'}
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    take(file)
                  }}
                />
              </label>
              {own ? (
                <button
                  type="button"
                  className="ui-iconbtn"
                  aria-label="Убрать обложку"
                  data-tip="Убрать обложку"
                  onClick={onRemove}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </div>
          ) : null}
          {error ? (
            <span className="ui-error" role="alert">
              <Icon name="i-alert" />
              {error}
            </span>
          ) : null}
        </div>
      </div>
      {depth > 0 ? (
        <div className="ui-drop-over" aria-hidden="true">
          <Icon name="i-img" />
          Отпустите, чтобы поставить обложку
        </div>
      ) : null}
    </section>
  )
}

/**
 * Карточка сторис: слайды, подпись под кружком, обложка и период показа.
 * Правки и загруженные картинки копятся в черновике и уходят одной кнопкой
 * «Сохранить»; включение показа — сразу, как у категорий. Новая сторис
 * (`/stories/new`) собирается той же формой и создаётся целиком, выключенной:
 * картинки грузятся без id сторис, ключи уходят вместе с ней.
 */
export function StoryScreen({ onToast }: StoryScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const creating = params.id === undefined
  const id = Number(params.id)

  const stories = useStories()
  const save = useSaveStory()
  const remove = useDeleteStory()
  const upload = useUploadStoryMedia()
  const [deleting, setDeleting] = useState(false)
  const [edits, setEdits] = useState<Draft | null>(null)
  const [uploading, setUploading] = useState(0)
  const [coverBusy, setCoverBusy] = useState(false)
  // Новые слайды получают свой номер: у них ещё нет id, а перетаскиванию нужен ключ.
  const nextUid = useRef(0)
  // Пустая подпись новой сторис краснеет, только когда из поля ушли.
  const [leftTitle, setLeftTitle] = useState(false)
  // Новая сторис встаёт последней — порядок считается по уже загруженным.
  const blank = useMemo<Story>(
    () => ({ ...newStory('', stories.data ?? []), views: 0, updatedAt: '' }),
    [stories.data],
  )

  useEffect(() => {
    setEdits(null)
  }, [id])

  const story = creating ? blank : stories.data?.find((s) => s.id === id)
  const dirty = edits !== null && story !== undefined && changed(edits, story)
  useUnsavedWarning(dirty || uploading > 0)

  if (stories.isPending) return <Loading what="сторис" />
  if (stories.isError) {
    return (
      <Failed
        what="сторис"
        error={stories.error}
        onRetry={() => {
          void stories.refetch()
        }}
      />
    )
  }
  if (creating && !can('stories', 'c')) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-stories" />
          <b>Создавать сторис роль не разрешает</b>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/stories')
            }}
          >
            К списку сторис
          </button>
        </div>
      </div>
    )
  }
  if (!story) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-stories" />
          <b>Сторис не найдена</b>
          <p>Возможно, её удалили.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/stories')
            }}
          >
            К списку сторис
          </button>
        </div>
      </div>
    )
  }

  const current: Story = story
  const draft = edits ?? toDraft(current)
  const canUpdate = can('stories', creating ? 'c' : 'u')
  const status = storyStatus(current, nowMs())
  const problem = draftError(draft)
  const titleMissing = draft.titleRu.trim() === '' && (!creating || leftTitle)
  // Сервер не включит сторис без слайдов; слайды должны быть уже сохранены.
  const cannotShow = !current.active && current.slides.length === 0

  /** Правка от ответа загрузки: черновик к этому времени мог измениться. */
  const editLater = (change: (base: Draft) => Partial<Draft>) => {
    setEdits((prev) => {
      const base = prev ?? toDraft(current)
      return { ...base, ...change(base) }
    })
  }

  async function uploadSlides(files: File[]) {
    setUploading((n) => n + files.length)
    for (const file of files) {
      try {
        const media = await upload.mutateAsync(file)
        nextUid.current += 1
        const slide = { ...newSlide(media), uid: `n${nextUid.current}` }
        editLater((base) => ({ slides: [...base.slides, slide] }))
      } catch (error) {
        onToast(storyFailureText(error))
      } finally {
        setUploading((n) => n - 1)
      }
    }
  }

  function uploadCover(file: File) {
    setCoverBusy(true)
    upload.mutate(file, {
      onSuccess: (media) => {
        editLater(() => ({ coverKey: media.key, coverUrl: media.url }))
      },
      onError: (error) => {
        onToast(storyFailureText(error))
      },
      onSettled: () => {
        setCoverBusy(false)
      },
    })
  }

  function toSave(base: Draft): StoryToSave {
    return {
      id: current.id,
      titleRu: base.titleRu.trim(),
      titleUz: base.titleUz.trim(),
      coverKey: base.coverKey,
      coverUrl: base.coverUrl,
      slides: base.slides.map(({ uid: _uid, ...slide }) => ({ ...slide, link: linkToSave(slide) })),
      ...periodFromInput(base.from, base.to),
      sort: Number(base.sort) || current.sort,
      active: current.active,
    }
  }

  function saveAll() {
    save.mutate(toSave(draft), {
      onSuccess: (saved) => {
        setEdits(null)
        if (creating) {
          onToast('Сторис создана — включите показ в карточке')
          void navigate(`/stories/${saved.id}`)
        } else onToast('Изменения сохранены')
      },
      onError: (error) => {
        onToast(storyFailureText(error))
      },
    })
  }

  function toggleActive(active: boolean) {
    save.mutate(
      { ...toSave(toDraft(current)), active },
      {
        onSuccess: () => {
          onToast(active ? 'Сторис включена' : 'Сторис выключена')
        },
        onError: (error) => {
          onToast(storyFailureText(error))
        },
      },
    )
  }

  function removeStory() {
    remove.mutate(current.id, {
      onSuccess: () => {
        onToast('Сторис удалена')
        void navigate('/stories')
      },
      onError: (error) => {
        setDeleting(false)
        onToast(storyFailureText(error))
      },
    })
  }

  const edit = (patch: Partial<Draft>) => {
    setEdits({ ...draft, ...patch })
  }

  return (
    <>
      {creating ? (
        <DetailHead
          backLabel="Сторис"
          onBack={() => {
            void navigate('/stories')
          }}
          title="Новая сторис"
          sub={null}
        />
      ) : (
        <DetailHead
          backLabel="Сторис"
          onBack={() => {
            void navigate('/stories')
          }}
          title={story.titleRu}
          avatar={<StoryCircle story={story} size={48} live={status === 'live'} />}
          sub={
            <>
              <span className="ui-status" data-tone={STATUS_TONE[status]}>
                {STATUS_TEXT[status]}
              </span>
              <span className="dot" aria-hidden="true" />
              <span>
                {withCountRu(story.slides.length, ['слайд', 'слайда', 'слайдов'])}
                {story.slides.length ? ` · ${totalSeconds(story.slides)} с` : ''}
              </span>
              <span className="dot" aria-hidden="true" />
              <span>{periodText(story)}</span>
              <span className="dot" aria-hidden="true" />
              <span>{withCountRu(story.views, ['просмотр', 'просмотра', 'просмотров'])}</span>
            </>
          }
          actions={
            <>
              {canUpdate && cannotShow ? (
                <span className="ui-tag" data-tone="warning" id="sto-show-why">
                  сначала сохраните слайды
                </span>
              ) : null}
              {canUpdate ? (
                <label className="ui-switch cat-active">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={story.active}
                    disabled={save.isPending || cannotShow}
                    aria-describedby={cannotShow ? 'sto-show-why' : undefined}
                    onChange={(e) => {
                      toggleActive(e.target.checked)
                    }}
                  />
                  Показывать в приложении
                </label>
              ) : (
                <span className="ui-tag">только просмотр</span>
              )}
              {can('stories', 'd') ? (
                <button
                  type="button"
                  className="ui-iconbtn ui-danger-icon"
                  aria-label="Удалить сторис"
                  data-tip="Удалить сторис"
                  data-tip-pos="bottom"
                  data-tip-align="end"
                  onClick={() => {
                    setDeleting(true)
                  }}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </>
          }
        />
      )}

      <div className="ui-detail-grid sto-grid">
        <div className="ui-detail-col">
          <StorySlides
            slides={draft.slides}
            canUpdate={canUpdate}
            uploading={uploading}
            onUpload={(files) => {
              void uploadSlides(files)
            }}
            onChange={(slides) => {
              edit({ slides })
            }}
          />
        </div>

        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="sto-title">
            <div className="ui-card-head">
              <h3 id="sto-title">Подпись под кружком</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-field">
                <label className="ui-label" htmlFor="sto-title-ru">
                  На русском
                </label>
                <input
                  id="sto-title-ru"
                  placeholder="Например, Кэшбэк на витамины"
                  className="ui-input"
                  lang="ru"
                  maxLength={STORY_TITLE_MAX}
                  disabled={!canUpdate}
                  aria-invalid={titleMissing ? true : undefined}
                  aria-describedby={titleMissing ? 'sto-title-ru-error' : undefined}
                  value={draft.titleRu}
                  onChange={(e) => {
                    edit({ titleRu: e.target.value })
                  }}
                  onBlur={() => {
                    setLeftTitle(true)
                  }}
                />
                {titleMissing ? (
                  <span className="ui-error" id="sto-title-ru-error">
                    <Icon name="i-alert" />
                    Без подписи кружок на главной пустой
                  </span>
                ) : null}
              </div>
              <div className="ui-field">
                <label className="ui-label" htmlFor="sto-title-uz">
                  На узбекском
                </label>
                <input
                  id="sto-title-uz"
                  placeholder="Masalan, Vitaminlarga keshbek"
                  className="ui-input"
                  lang="uz"
                  maxLength={STORY_TITLE_MAX}
                  disabled={!canUpdate}
                  value={draft.titleUz}
                  onChange={(e) => {
                    edit({ titleUz: e.target.value })
                  }}
                />
                <span className="ui-hint">Два-три слова — длинная подпись обрежется</span>
              </div>
            </div>
          </section>

          <StoryCover
            draft={draft}
            canUpdate={canUpdate}
            busy={coverBusy}
            onUpload={uploadCover}
            onRemove={() => {
              edit({ coverKey: '', coverUrl: '' })
            }}
          />

          <section className="ui-card" aria-labelledby="sto-period">
            <div className="ui-card-head">
              <h3 id="sto-period">Показ</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-form-row">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="sto-from">
                    С
                  </label>
                  <DatePicker
                    id="sto-from"
                    label="Начало показа"
                    disabled={!canUpdate}
                    value={draft.from}
                    onChange={(from) => {
                      edit({ from })
                    }}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="sto-to">
                    По
                  </label>
                  <DatePicker
                    id="sto-to"
                    label="Конец показа"
                    disabled={!canUpdate}
                    invalid={periodError(draft.from, draft.to) !== ''}
                    min={draft.from || undefined}
                    value={draft.to}
                    onChange={(to) => {
                      edit({ to })
                    }}
                  />
                </div>
              </div>
              {periodError(draft.from, draft.to) ? (
                <span className="ui-error">
                  <Icon name="i-alert" />
                  {periodError(draft.from, draft.to)}
                </span>
              ) : (
                <span className="ui-hint">Пустые даты — показ без срока</span>
              )}
              <div className="ui-field">
                <label className="ui-label" htmlFor="sto-sort">
                  Порядок на главной
                </label>
                <input
                  id="sto-sort"
                  className="ui-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  disabled={!canUpdate}
                  value={draft.sort}
                  onChange={(e) => {
                    edit({ sort: e.target.value })
                  }}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      {canUpdate ? (
        <div className="ui-savebar" data-dirty={dirty || undefined}>
          <span className="state" role="status">
            {(creating || dirty) && problem
              ? problem
              : dirty
                ? 'Есть несохранённые изменения'
                : 'Все изменения сохранены'}
          </span>
          {dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setEdits(null)
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!dirty || problem !== '' || uploading > 0 || save.isPending}
            aria-busy={save.isPending}
            onClick={saveAll}
          >
            {creating ? 'Создать сторис' : 'Сохранить'}
          </button>
        </div>
      ) : null}

      {deleting ? (
        <Modal
          title="Удалить сторис?"
          onClose={() => {
            setDeleting(false)
          }}
        >
          <p>
            «{story.titleRu}» пропадёт с главной приложения. Вернуть её можно только созданием
            заново.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setDeleting(false)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={removeStory}
            >
              Удалить
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
